import { type UploadMethod } from "@/hooks/uploads/uploadMethodsConfig";

const MASCOT_SRC = "/genie-mascots/role/support.png";

interface MethodExplainer {
  whatItDoes: string;
  whenToUse: string;
  examples: string[];
}

const EXPLAINERS: Record<UploadMethod, MethodExplainer> = {
  bulk: {
    whatItDoes:
      "Drop a stack of files (PDFs, docs, audio, images) all at once. Each one becomes its own knowledge document, processed in parallel.",
    whenToUse:
      "Use when you're seeding a brand-new genie from a pile of existing material and don't want to upload them one-by-one.",
    examples: ["Onboarding pack", "Existing FAQ folder", "Old training material"],
  },
  pdf: {
    whatItDoes:
      "Pick a PDF from your computer. We extract the full text — including tables and structured sections — and add it as one searchable document.",
    whenToUse: "Best for things you already have as a PDF.",
    examples: ["Product manual", "Policy doc", "Pricing sheet", "Spec sheet"],
  },
  url: {
    whatItDoes:
      "Paste a direct link to a PDF that lives somewhere on the internet. We download it, extract the text, and add it — no need to save the file locally first.",
    whenToUse:
      "Best when the PDF lives on a partner's site, an open-data source, or your own CMS.",
    examples: ["Public report", "Partner spec sheet", "Hosted brochure"],
  },
  youtube: {
    whatItDoes:
      "Paste any YouTube URL. We pull the transcript and add the spoken content as a knowledge document.",
    whenToUse:
      "Great when the knowledge is already on video — your team's webinars, training, talks, or podcast episodes.",
    examples: ["Webinar recording", "Training video", "Conference talk", "Podcast episode"],
  },
  website: {
    whatItDoes:
      "Give us one page URL and we'll grab the clean text content from that single page — stripping nav, ads, and footer junk.",
    whenToUse: "Best for one specific page that already has good content.",
    examples: ["FAQ page", "Pricing page", "About page", "Blog post"],
  },
  "website-brightdata": {
    whatItDoes:
      "Same as Website, but the page is fetched through an alternate extraction engine — better at getting past JS rendering, CAPTCHAs, and bot protection.",
    whenToUse: "Try this if a page fails or comes back empty via the regular Website method.",
    examples: ["FAQ page", "Pricing page", "About page", "Blog post"],
  },
  audio: {
    whatItDoes:
      "Upload an MP3, WAV, M4A, OGG, or WebM file. We transcribe what was said and add the transcript to the knowledge base.",
    whenToUse: "Use for any audio with content worth capturing.",
    examples: ["Sales call recording", "Voice memo", "Podcast episode", "Interview"],
  },
  document: {
    whatItDoes:
      "Upload a Word doc, plain text, CSV, or DOCX file. We pull the text out and add it as a knowledge document.",
    whenToUse: "For anything you've written outside of PDF.",
    examples: ["Internal FAQ doc", "Exported spreadsheet", "Script", "Meeting notes"],
  },
  image: {
    whatItDoes:
      "Upload a photo, screenshot, or scan and we'll read any text in the image (OCR) and add it as a knowledge document.",
    whenToUse: "Use when the content only exists as an image.",
    examples: ["Screenshot of a policy", "Scanned form", "Photo of a printed sign", "Whiteboard"],
  },
  video: {
    whatItDoes:
      "Upload an MP4, MOV, WebM, or MKV file (up to 50MB), or paste a URL for larger videos hosted elsewhere. We extract the audio, transcribe what was said, and add the transcript to the knowledge base — the video itself isn't kept.",
    whenToUse: "Use for any video with spoken content worth capturing.",
    examples: ["Recorded webinar", "Training video", "Screen recording walkthrough", "Interview"],
  },
  notes: {
    whatItDoes:
      "Type or paste text directly — no file required. Whatever you write gets saved as a knowledge document the genie can use.",
    whenToUse: "Fastest way to capture something in your head.",
    examples: ["Quick policy", "Phone script", "Answer template", "Internal fact"],
  },
  "web-scraping": {
    whatItDoes:
      "Give us a starting URL and we'll crawl linked pages on the same site, pulling clean content from each into separate documents.",
    whenToUse:
      "Use when you want a whole site or section — not just one page — ingested in bulk.",
    examples: ["Entire docs site", "Help centre", "Product catalogue", "Knowledge base"],
  },
  "website-crawl": {
    whatItDoes:
      "Same as Web Scraping, but pages are fetched through an alternate extraction engine — better at getting past JS rendering, CAPTCHAs, and bot protection while crawling.",
    whenToUse: "Try this if a site crawl fails or comes back incomplete via the regular Web Scraping method.",
    examples: ["Entire docs site", "Help centre", "Product catalogue", "Knowledge base"],
  },
  "quick-research": {
    whatItDoes:
      "Ask a question or describe a topic. The Genie spends a minute or two pulling together a focused brief from the web, then saves it as a knowledge document.",
    whenToUse:
      "Best when you need a clean answer on a specific topic without doing the research yourself.",
    examples: [
      "Latest pricing on competitors",
      "Summary of a regulation",
      "Industry primer",
    ],
  },
  "deep-research": {
    whatItDoes:
      "Same idea as Quick Research, but the Genie spends 5–15 minutes gathering, cross-checking, and synthesising from many sources before writing.",
    whenToUse:
      "Use when you need a thorough briefing — not just a summary.",
    examples: [
      "Competitive analysis",
      "Market sizing",
      "Domain primer",
      "Decision memo",
    ],
  },
  "cloud-storage": {
    whatItDoes:
      "Connect Google Drive or OneDrive once, then pick folders or files to import. Files stay in your cloud — content gets pulled in.",
    whenToUse:
      "Best when your knowledge already lives in shared cloud folders and you don't want to re-upload it.",
    examples: ["Shared Drive folder", "OneDrive team folder", "Mixed file types"],
  },
  "custom-integration": {
    whatItDoes:
      "Need to pull knowledge from a system we don't support yet — your CRM, a database, an internal API, a proprietary tool? Book 15 minutes with our team and we'll scope a custom integration.",
    whenToUse:
      "Use when your knowledge lives somewhere bespoke and the standard methods don't fit.",
    examples: ["Internal CRM", "Custom database", "Private API", "Legacy system"],
  },
};

interface UploadMethodExplainerProps {
  method: UploadMethod;
  methodLabel: string;
}

export const UploadMethodExplainer = ({ method, methodLabel }: UploadMethodExplainerProps) => {
  const explainer = EXPLAINERS[method];
  if (!explainer) return null;

  return (
    <div className="bg-gradient-to-r from-genie-primary/5 to-genie-secondary/5 rounded-xl border border-genie-primary/15 p-4 mb-5">
      <div className="flex items-start gap-3">
        <img
          src={MASCOT_SRC}
          alt=""
          className="w-9 h-9 object-contain flex-shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
              What {methodLabel} does
            </span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">{explainer.whatItDoes}</p>
          <p className="text-sm text-gray-600 leading-relaxed mt-2">
            <span className="font-semibold text-gray-700">When to use it: </span>
            {explainer.whenToUse}
          </p>
          {explainer.examples.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {explainer.examples.map((ex) => (
                <span
                  key={ex}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-white border border-gray-200 text-gray-600"
                >
                  {ex}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadMethodExplainer;
